import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check';
import LocationOffIcon from '@mui/icons-material/LocationOff';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { Autocomplete, Box, Button, CircularProgress, Grid, InputAdornment, Popper, TextField } from '@mui/material';
import { GoogleMap } from '@react-google-maps/api';
import React from 'react';

import { WindowNamespace } from '../utils';
import { importMapsLibrary, loadGoogleMaps } from '../utils/googleMaps';
import { placeToLegacy, PLACE_FIELDS } from './placeAdapter';

const defaultHeight = 350;

const SEARCH_DEBOUNCE_MS = 700;
const MIN_QUERY_LENGTH = 3;

const PlacesAutocompletePopper = function(props) {
    return <Popper {...props} placement="bottom-start" style={{ ...props.style, zIndex: 2000 }} />;
};

// AutocompleteSuggestion replaces the legacy AutocompleteService, whose
// structured_formatting.main_text / .secondary_text are now mainText / secondaryText.
// The normalized shape is unchanged so the MUI Autocomplete below is untouched.
const normalizeAutocompletePrediction = (suggestion) => {
    const prediction = suggestion.placePrediction;
    const text = prediction.text?.text || '';
    return {
        place_id: prediction.placeId,
        primary_text: prediction.mainText?.text || text,
        secondary_text: prediction.secondaryText?.text || '',
        description: text,
        // Kept so the details fetch can go through placePrediction.toPlace(),
        // which is what closes the billing session. FetchFieldsRequest itself
        // takes no session token.
        prediction,
    };
};

function PlaceSearch(props) {
    const { googleApiKey, ns } = props;
    const gmapNs = React.useMemo(() => WindowNamespace(ns), [ns]);
    const [showMap, setShowMap] = React.useState(false);
    const [isCustom, setIsCustom] = React.useState(false);
    const [isGoogleReady, setIsGoogleReady] = React.useState(false);
    const [isInputFocused, setIsInputFocused] = React.useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);
    const [predictions, setPredictions] = React.useState([]);
    const [predictionsLoading, setPredictionsLoading] = React.useState(false);
    const [hasSearched, setHasSearched] = React.useState(false);
    const [selectedPredictionId, setSelectedPredictionId] = React.useState(null);
    const [selectedPlace, setSelectedPlace] = React.useState(null);
    const [placeLoading, setPlaceLoading] = React.useState(false);
    const inputRef = React.useRef(null);
    const useCustomBtRef = React.useRef(null);
    const useGooglePlaceBtRef = React.useRef(null);
    const [mapLocation, setMapLocation] = React.useState(null);
    const [lastLocationSelected, setLastLocationSelected] = React.useState(null);
    const predictionsRequestRef = React.useRef(0);
    const placeRequestRef = React.useRef(0);
    const predictionCacheRef = React.useRef(new Map());
    const lastRequestedQueryRef = React.useRef('');
    const suppressNextSearchRef = React.useRef(false);
    // One autocomplete session = the keystrokes of one search plus the Place
    // Details that follows. Google bills the session, not each keystroke.
    const sessionTokenRef = React.useRef(null);

    const mapClickMode = !!props.mapClickMode;
    if (mapClickMode && !showMap) {
        setShowMap(true);
    }

    const disableUseCustomBt = !showMap;
    const disableUseGooglePlaceBt = !selectedPlace || placeLoading;

    let containerStyle = {
        width: '100%',
        height: `${props.height || defaultHeight}px`,
        marginTop: '40px',
    };

    const getMapCenter = () => {
        if (props.place && props.place.geometry && props.place.geometry.location) {
            return props.place.geometry.location;
        } else {
            return {
                lat: props.lat || 0.0,
                lng: props.lng || 0.0,
            };
        }
    };
    const center = getMapCenter();

    // One loader for the whole SPA -- see utils/googleMaps.js. The <LoadScript>
    // this replaces was one of six mounts with disagreeing `libraries`, which is
    // what Google's "included the API multiple times" warning was reporting.
    React.useEffect(() => {
        if (!googleApiKey) return undefined;
        let mounted = true;
        loadGoogleMaps(googleApiKey)
            .then(() => {
                if (!mounted) return;
                gmapNs.set('google', window['google']);
                setIsGoogleReady(true);
            })
            .catch((e) => console.warn('PlaceSearch: Google Maps failed to load', e));
        return () => {
            mounted = false;
        };
    }, [googleApiKey, gmapNs]);

    const clearClickListener = () => {
        const google = gmapNs.get('google');
        const clickLsr = gmapNs.get('clickLsr');
        if (clickLsr) {
            google.maps.event.removeListener(clickLsr);
        }
    };

    const onMapLoad = function (m) {
        const google = gmapNs.get('google');
        if (!google) {
            return;
        }
        gmapNs?.set('map', m);
        gmapNs?.set(
            'marker',
            new google.maps.Marker({
                map: m,
                title: props.name,
                position: new google.maps.LatLng(lastLocationSelected ? lastLocationSelected : center),
                // position: new google.maps.LatLng(center.lat, center.lng)
            })
        );

        clearClickListener();
        if (props.mapClickMode && props.onLocationSelected) {
            gmapNs.set('clickLsr', google.maps.event.addListener(m, 'click', onMapClick));
        }
    };

    const onMapClick = (mev) => {
        setMapLocation(mev.latLng);
        setLastLocationSelected(mev.latLng);

        const google = gmapNs.get('google');
        const map = gmapNs.get('map');
        const marker = gmapNs.get('marker');
        marker && marker.setMap(null);
        gmapNs.set(
            'marker',
            new google.maps.Marker({
                map: gmapNs.get('map'),
                title: props.name,
                position: mev.latLng,
            })
        );
        map.panTo(mev.latLng);
    };

    const handleLocationSelected = () => {
        console.log('XYZ handleLocationSelected', mapLocation, props.mapClickMode);
        props.onLocationSelected && props.onLocationSelected(mapLocation);
        setShowMap(false);
    };

    const syncPlaceOnMap = React.useCallback((place) => {
        const google = gmapNs.get('google');
        const map = gmapNs.get('map');
        if (!place?.geometry?.location) {
            return;
        }
        let marker = gmapNs.get('marker');
        if (marker) {
            marker.setMap(null);
        }
        if (google && map) {
            gmapNs.set(
                'marker',
                new google.maps.Marker({
                    map: map,
                    title: place.name,
                    position: place.geometry.location,
                })
            );
            map.panTo(place.geometry.location);
        }

        if (props.onPlaceChanged) {
            props.onPlaceChanged(place);
        }
        setLastLocationSelected(place.geometry.location);
        props.onNameChange && props.onNameChange(place.name);
    }, [gmapNs, props]);

    const fetchPredictions = React.useCallback((inputValue) => {
        const google = gmapNs.get('google');
        const normalizedQuery = inputValue?.trim();
        if (!google || !normalizedQuery || normalizedQuery.length < MIN_QUERY_LENGTH || isCustom || mapClickMode) {
            setPredictions([]);
            setPredictionsLoading(false);
            setHasSearched(false);
            setIsDropdownOpen(false);
            lastRequestedQueryRef.current = '';
            return;
        }
        if (lastRequestedQueryRef.current === normalizedQuery) {
            return;
        }
        const cachedPredictions = predictionCacheRef.current.get(normalizedQuery);
        if (cachedPredictions) {
            lastRequestedQueryRef.current = normalizedQuery;
            setPredictions(cachedPredictions);
            setPredictionsLoading(false);
            setHasSearched(true);
            setIsDropdownOpen(true);
            return;
        }
        const reqId = predictionsRequestRef.current + 1;
        predictionsRequestRef.current = reqId;
        lastRequestedQueryRef.current = normalizedQuery;
        setPredictionsLoading(true);
        setHasSearched(false);
        const map = gmapNs.get('map');
        (async () => {
            let normalizedPredictions = [];
            try {
                const { AutocompleteSessionToken, AutocompleteSuggestion } =
                    await importMapsLibrary('places', googleApiKey);
                // A session token groups the keystrokes of one search plus the
                // Place Details that follows into a single billable session. The
                // legacy AutocompleteService call had none, so every keystroke was
                // billed on its own -- this migration makes autocomplete cheaper,
                // not dearer. The token is replaced after each selection; see
                // resetAutocompleteSession.
                if (!sessionTokenRef.current) {
                    sessionTokenRef.current = new AutocompleteSessionToken();
                }
                const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
                    input: normalizedQuery,
                    sessionToken: sessionTokenRef.current,
                    locationBias: map?.getBounds?.() || undefined,
                });
                normalizedPredictions = (suggestions || [])
                    .filter((s) => s.placePrediction)
                    .map(normalizeAutocompletePrediction);
            } catch (e) {
                // Matches the old non-OK status branch: an empty dropdown, never a throw.
                console.warn('PlaceSearch: autocomplete failed', e);
            }
            if (predictionsRequestRef.current !== reqId) {
                return;
            }
            predictionCacheRef.current.set(normalizedQuery, normalizedPredictions);
            setPredictions(normalizedPredictions);
            setPredictionsLoading(false);
            setHasSearched(true);
            setIsDropdownOpen(true);
        })();
    }, [gmapNs, googleApiKey, isCustom, mapClickMode]);

    const loadPlaceDetails = React.useCallback(async (selection) => {
        const placeId = typeof selection === 'string' ? selection : selection?.place_id;
        if (!placeId || !googleApiKey) {
            return;
        }
        const reqId = placeRequestRef.current + 1;
        placeRequestRef.current = reqId;
        setPlaceLoading(true);
        try {
            const { Place } = await importMapsLibrary('places', googleApiKey);
            // toPlace() when the id came from a suggestion: that is what ties the
            // details call to the autocomplete session so the pair bills as one.
            const prediction = typeof selection === 'object' ? selection?.prediction : null;
            const place = prediction?.toPlace ? prediction.toPlace() : new Place({ id: placeId });
            await place.fetchFields({ fields: PLACE_FIELDS });
            // The session ends with the details call either way.
            sessionTokenRef.current = null;
            if (placeRequestRef.current !== reqId) {
                return;
            }
            // Adapted immediately: everything downstream -- syncPlaceOnMap,
            // PlaceWrapper, the place_data PUT -- reads the legacy shape.
            const legacy = placeToLegacy(place);
            setSelectedPlace(legacy);
            syncPlaceOnMap(legacy);
        } catch (e) {
            // A rejection here replaces the old non-OK PlacesServiceStatus branch.
            if (placeRequestRef.current === reqId) {
                console.warn('PlaceSearch: could not load place details', placeId, e);
                setSelectedPlace(null);
            }
        } finally {
            if (placeRequestRef.current === reqId) {
                setPlaceLoading(false);
            }
        }
    }, [googleApiKey, syncPlaceOnMap]);

    const onNameChange = (ev) => {
        suppressNextSearchRef.current = false;
        setSelectedPredictionId(null);
        setSelectedPlace(null);
        setHasSearched(false);
        setIsDropdownOpen(false);
        lastRequestedQueryRef.current = '';
        props.clearError && props.clearError();
        props.onNameChange && props.onNameChange(ev.target.value);
    };

    const handleInputFocus = () => {
        setIsInputFocused(true);
        if (isCustom) {
            return;
        } else {
            setShowMap(true);
            if (props.onMapToggle) {
                props.onMapToggle(true);
            }
        }
    };

    const handleInputBlur = () => {
        setTimeout(() => {
            setIsInputFocused(false);
        }, 150);
    };

    const handleEmptyPlaceSelected = () => {
        setIsCustom(true);
        setShowMap(false);
        setIsInputFocused(false);
        setIsDropdownOpen(false);
        setHasSearched(false);
        setPredictions([]);
        setSelectedPredictionId(null);
        setSelectedPlace(null);
        props.onPlaceSelected && props.onPlaceSelected(null);
        setTimeout(() => {
            inputRef.current?.querySelector('input').focus();
        }, 250); // timeout time is just a wild guess
        // TODO: replace with something more reliable
        // Objective is to focus on input after React has updated DOM
    };

    const handlePredictionSelected = (prediction) => {
        suppressNextSearchRef.current = true;
        setIsCustom(false);
        setSelectedPredictionId(prediction.place_id);
        setShowMap(true);
        setIsDropdownOpen(false);
        loadPlaceDetails(prediction);
    };

    const handlePlaceSelected = () => {
        setIsInputFocused(false);
        setIsDropdownOpen(false);
        setPredictions([]);
        if (isCustom) {
            setIsCustom(false);
            setShowMap(true);
            setTimeout(() => {
                inputRef.current?.querySelector('input').focus();
            }, 250); // comment from handleEmptyPlaceSelected applies here too
        } else {
            setShowMap(false);
        }
        props.onPlaceSelected && props.onPlaceSelected(selectedPlace);
    };

    React.useEffect(() => {
        if (!isGoogleReady || !isInputFocused || isCustom || mapClickMode) {
            setPredictions([]);
            setPredictionsLoading(false);
            setHasSearched(false);
            setIsDropdownOpen(false);
            return;
        }
        if (suppressNextSearchRef.current) {
            return;
        }
        const term = props.value?.trim();
        if (!term || term.length < MIN_QUERY_LENGTH) {
            setPredictions([]);
            setPredictionsLoading(false);
            setHasSearched(false);
            setIsDropdownOpen(false);
            return;
        }
        const timer = setTimeout(() => {
            fetchPredictions(term);
        }, SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [fetchPredictions, isCustom, isGoogleReady, isInputFocused, mapClickMode, props.value]);

    const errorAttrs = {};
    if (props.error && props.helperText) {
        errorAttrs.error = true;
        errorAttrs.helperText = props.helperText;
    }

    const handleCancelUseMapMode = () => {
        props.handleCancelUseMapClick();
        setShowMap(false);
    };

    const disableUseLocationButton = !mapLocation;
    const showPredictions = isDropdownOpen && isGoogleReady && !mapClickMode && !isCustom && isInputFocused && (predictionsLoading || predictions.length > 0 || hasSearched);
    const selectedPrediction = predictions.find((prediction) => prediction.place_id === selectedPredictionId) || null;

    return (
        <>
            <Grid container direction="row" alignItems="flex-start">
                <Grid item xs={props.isSmallScreen ? 12 : 8}>
                    <Autocomplete
                        freeSolo
                        PopperComponent={PlacesAutocompletePopper}
                        disablePortal={props.isSmallScreen}
                        open={showPredictions}
                        options={predictions}
                        value={selectedPrediction}
                        inputValue={props.value || ''}
                        loading={predictionsLoading}
                        disabled={props.disabled}
                        filterOptions={(options) => options}
                        isOptionEqualToValue={(option, value) => option.place_id === value.place_id}
                        onClose={(ev, reason) => {
                            if (reason === 'escape' || reason === 'blur' || reason === 'toggleInput') {
                                setIsDropdownOpen(false);
                            }
                        }}
                        getOptionLabel={(option) => {
                            if (typeof option === 'string') {
                                return option;
                            }
                            return option.primary_text || option.description || '';
                        }}
                        noOptionsText="No Google Place suggestions found for this text."
                        loadingText="Searching Google Places..."
                        onInputChange={(ev, value, reason) => {
                            if (reason === 'input') {
                                onNameChange({ target: { value } });
                            } else if (reason === 'clear') {
                                setIsDropdownOpen(false);
                                setPredictions([]);
                                setSelectedPredictionId(null);
                                setSelectedPlace(null);
                                setHasSearched(false);
                                lastRequestedQueryRef.current = '';
                                props.clearError && props.clearError();
                                props.onNameChange && props.onNameChange('');
                            }
                        }}
                        onChange={(ev, prediction) => {
                            if (prediction?.place_id) {
                                handlePredictionSelected(prediction);
                            }
                        }}
                        renderOption={(optionProps, prediction) => (
                            <li {...optionProps} key={prediction.place_id}>
                                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%" gap={1}>
                                    <Box>
                                        <Box>{prediction.primary_text || prediction.description}</Box>
                                        {prediction.secondary_text ? (
                                            <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                                                {prediction.secondary_text}
                                            </Box>
                                        ) : null}
                                    </Box>
                                    {selectedPredictionId === prediction.place_id && selectedPlace ? <CheckIcon color="primary" fontSize="small" /> : null}
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                {...errorAttrs}
                                variant="filled"
                                required
                                label={props.name}
                                onFocus={handleInputFocus}
                                onBlur={handleInputBlur}
                                inputRef={(node) => {
                                    inputRef.current = node ? { querySelector: () => node } : null;
                                }}
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment
                                            position="start"
                                            sx={{
                                                alignSelf: 'center',
                                                mt: '0 !important',
                                            }}
                                        >
                                            {isCustom ? <LocationOffIcon /> : <LocationOnIcon />}
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <>
                                            {placeLoading ? (
                                                <InputAdornment position="end">
                                                    <CircularProgress size={18} />
                                                </InputAdornment>
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                sx={{
                                    '& .MuiInputAdornment-root': {
                                        alignSelf: 'center',
                                        marginTop: '0 !important',
                                    },
                                }}
                            />
                        )}
                    />
                </Grid>
                {!mapClickMode ? (
                    <Grid item xs={props.isSmallScreen ? 6 : 2}>
                        <Box mt={2} ml={1} mr={1}>
                            <Button ref={useCustomBtRef} onClick={handleEmptyPlaceSelected} variant="outlined" size="small" fullWidth disabled={disableUseCustomBt}>
                                Use custom
                            </Button>
                        </Box>
                    </Grid>
                ) : null}
                {!mapClickMode ? (
                    <Grid item xs={props.isSmallScreen ? 6 : 2}>
                        <Box mt={2}>
                            <Button ref={useGooglePlaceBtRef} onClick={handlePlaceSelected} variant="contained" color="primary" size="small" fullWidth disabled={disableUseGooglePlaceBt}>
                                Use Google Place
                            </Button>
                        </Box>
                    </Grid>
                ) : null}
                {mapClickMode ? (
                    <>
                        <Grid item xs={props.isSmallScreen ? 6 : 2}>
                            <Box m={2}>
                                <Button onClick={handleLocationSelected} disabled={disableUseLocationButton} variant="contained" color="primary" size="small" fullWidth>
                                    Use location
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={props.isSmallScreen ? 6 : 2}>
                            <Box m={2}>
                                <Button onClick={handleCancelUseMapMode} variant="contained" color="secondary" size="small" fullWidth>
                                    <ClearIcon />
                                    &nbsp;cancel
                                </Button>
                            </Box>
                        </Grid>
                    </>
                ) : null}
                <Grid item xs={12}>
                    {showMap && isGoogleReady ? <GoogleMap onLoad={onMapLoad} mapContainerStyle={containerStyle} zoom={15} center={lastLocationSelected ? lastLocationSelected : getMapCenter(props)} /> : null}
                </Grid>
            </Grid>
        </>
    );
}

export default React.memo(PlaceSearch);
